<template>
    <div class="as-page">
        <div class="as-header">
            <div class="as-header-left">
                <div class="as-icon-wrap"><i class="fas fa-boxes"></i></div>
                <h2 class="as-title">Assets</h2>
                <span class="as-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <div class="as-header-right">
                <div class="as-filter-pills">
                    <button v-for="opt in filterOptions" :key="opt.id" class="as-pill" :class="{ 'as-pill-active': allocatedType === opt.id }" @click="handleAllocatedTypeChange(opt.id)">
                        {{ opt.title }}
                    </button>
                </div>
                <button class="as-add-btn" @click="showForm = true">
                    <i class="fas fa-plus"></i>
                    Add Asset
                </button>
            </div>
        </div>

        <RightSideModal :show="showForm" :title="editMode ? 'Edit Asset' : 'New Asset'" @close="closeForm">
            <Form :fields="createFields" title="Asset" submitUrl="/assets" updateRootUrl="/assets"
                @success="handleFormSuccess" @cancel="closeForm" :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <div v-if="loading" class="as-loading"><div class="as-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!items.length" class="as-empty">
            <div class="as-empty-icon"><i class="fas fa-boxes"></i></div>
            <h3>No Asset Available</h3>
        </div>
        <div v-else class="as-grid">
            <div class="as-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <!-- Asset image -->
                <div class="as-card-img-wrap" v-if="item.assetImage">
                    <img class="as-card-img" :src="serverURL + item.assetImage" :alt="item.name" />
                </div>
                <div class="as-card-top">
                    <div class="as-card-icon" :class="asColor(idx)" v-if="!item.assetImage"><i class="fas fa-box"></i></div>
                    <div class="as-card-info">
                        <h4 class="as-card-name">{{ item.name }}</h4>
                        <span class="as-card-serial" v-if="item.serialNo"><i class="fas fa-barcode"></i> {{ item.serialNo }}</span>
                    </div>
                    <span class="as-badge" :class="item.isActive ? 'as-badge-active' : 'as-badge-inactive'">{{ item.isActive ? 'Active' : 'Inactive' }}</span>
                </div>
                <div class="as-card-details" v-if="item.price || item.billNo || item.billDate">
                    <div class="as-detail" v-if="item.price">
                        <span class="as-detail-label">Price</span>
                        <span class="as-detail-val">&#8377; {{ Number(item.price).toLocaleString('en-IN') }}</span>
                    </div>
                    <div class="as-detail" v-if="item.billNo">
                        <span class="as-detail-label">Bill No</span>
                        <span class="as-detail-val">{{ item.billNo }}</span>
                    </div>
                    <div class="as-detail" v-if="item.billDate">
                        <span class="as-detail-label">Bill Date</span>
                        <span class="as-detail-val">{{ item.billDate }}</span>
                    </div>
                </div>
                <div class="as-card-actions">
                    <button class="as-action-btn as-btn-edit" @click="enableEditing(item)"><i class="fas fa-pen"></i> Edit</button>
                    <button class="as-action-btn as-btn-barcode" @click="openBarcode(item)"><i class="fas fa-barcode"></i> Barcode</button>
                    <button class="as-action-btn as-btn-logs" @click="viewLogs(item.id)"><i class="fas fa-history"></i> Logs</button>
                    <a v-if="item.billCopy" class="as-action-btn as-btn-bill" :href="serverURL + item.billCopy" target="_blank"><i class="fas fa-file-invoice"></i> Bill</a>
                    <button class="as-action-btn as-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                </div>
            </div>
        </div>

        <!-- Barcode Modal -->
        <transition name="bc-fade">
            <div class="bc-overlay" v-if="barcodeModal.show" @click.self="barcodeModal.show = false">
                <div class="bc-modal">
                    <div class="bc-modal-header">
                        <div class="bc-modal-icon"><i class="fas fa-barcode"></i></div>
                        <span class="bc-modal-title">Asset Barcode</span>
                        <button class="bc-close" @click="barcodeModal.show = false"><i class="fas fa-times"></i></button>
                    </div>

                    <!-- Printable label area -->
                    <div class="bc-label" id="bc-print-area" ref="printArea">
                        <div class="bc-label-header">
                            <span class="bc-company-name">ASSET TAG</span>
                        </div>
                        <div class="bc-asset-name">{{ barcodeModal.item.name }}</div>
                        <div class="bc-meta-row">
                            <div class="bc-meta" v-if="barcodeModal.item.price">
                                <span class="bc-meta-label">Price</span>
                                <span class="bc-meta-val">₹ {{ Number(barcodeModal.item.price).toLocaleString('en-IN') }}</span>
                            </div>
                            <div class="bc-meta">
                                <span class="bc-meta-label">Added On</span>
                                <span class="bc-meta-val">{{ fmtDate(barcodeModal.item.createdAt) }}</span>
                            </div>
                            <div class="bc-meta" v-if="barcodeModal.item.serialNo">
                                <span class="bc-meta-label">Serial No</span>
                                <span class="bc-meta-val">{{ barcodeModal.item.serialNo }}</span>
                            </div>
                            <div class="bc-meta" v-if="barcodeModal.item.allocatedTo">
                                <span class="bc-meta-label">Allocated To</span>
                                <span class="bc-meta-val">{{ barcodeModal.item.allocatedTo }}</span>
                            </div>
                        </div>
                        <div class="bc-svg-wrap">
                            <svg ref="barcodesvg"></svg>
                        </div>
                        <div class="bc-code-text">{{ barcodeModal.codeValue }}</div>
                    </div>

                    <div class="bc-actions">
                        <button class="bc-btn bc-btn-download" @click="downloadBarcode">
                            <i class="fas fa-download"></i> Download PNG
                        </button>
                        <button class="bc-btn bc-btn-print" @click="printBarcode">
                            <i class="fas fa-print"></i> Print
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapState } from "vuex"
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import { createField } from "../../utils/common"
import { PERMISSION_CODES } from "../../store/permission"
import { http, serverURL } from "../../utils/http"
import { ASSET_FILTER_TYPES } from "../../utils/constants"
import JsBarcode from "jsbarcode"

export default {
    data() {
        return {
            createFields: [createField("Name*", "name", "text", true), createField("Description", "description", "text", false)],
            loading: true,
            initialized: false,
            showForm: false,
            editMode: false,
            editData: null,
            items: [],
            serverURL,
            PERMISSION_CODES,
            filterOptions: [
                { id: ASSET_FILTER_TYPES.ALL, title: "All" },
                { id: ASSET_FILTER_TYPES.ALLOCATED, title: "Allocated" },
                { id: ASSET_FILTER_TYPES.NOT_ALLOCATED, title: "Unallocated" },
            ],
            allocatedType: ASSET_FILTER_TYPES.ALL,
            barcodeModal: {
                show: false,
                item: {},
                codeValue: "",
            },
        }
    },
    components: { Form, RightSideModal },
    mounted() { this.init() },
    computed: { ...mapState("misc", ["companies"]) },
    watch: {
        companies() { this.init() },
    },
    methods: {
        asColor(i) { return ["as-clr-blue","as-clr-purple","as-clr-teal","as-clr-amber","as-clr-rose"][i % 5] },
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        viewLogs(id) { window.open(`assets/${id}/logs`) },
        async handleAllocatedTypeChange(type) {
            this.allocatedType = type
            this.$router.replace({ query: { ...this.$route.query, type } }).catch(() => {})
            await this.fetchData()
        },
        async init() {
            if (this.initialized) return
            if (!this.companies.length) { this.loading = false; return }
            try {
                this.allocatedType = this.$route.query.type || ASSET_FILTER_TYPES.ALL
                const categories = await this.fetchAssetCategoryMaster()
                this.createFields = [
                    createField("Name*", "name", "text", true),
                    createField("Description", "description", "text", false),
                    createField("Serial No*", "serialNo", "text", true),
                    createField("Asset Category*", "assetCategoryId", "select", true, "", categories.map(c => ({ id: c.id, title: c.name }))),
                    createField("Company*", "companyId", "select", true, "", this.companies.map(c => ({ id: c.id, title: c.name }))),
                    createField("Allow Multiple User Allocation", "allowMultipleUserAllocation", "switch", false, false),
                    createField("Is Active", "isActive", "switch", false, true),
                    createField("Price*", "price", "number", true),
                    createField("Asset Image", "assetImage", "image", false),
                    createField("Bill No", "billNo", "text", false),
                    createField("Bill Date", "billDate", "date", false),
                    createField("Copy of Bill", "billCopy", "file", false),
                ]
                await this.fetchData()
                this.initialized = true
            } catch (err) { console.log("error in initializing assets", err); this.loading = false }
        },
        async fetchData() {
            this.loading = true
            try { const r = await http.get(`/assets?type=${this.allocatedType}`); this.items = r.data.result || [] } catch(e) { console.log(e) }
            this.loading = false
        },
        async fetchAssetCategoryMaster() { const res = await http.get("/asset-categories"); return res.data.result },
        async deleteItem(id) {
            if (!confirm("Delete this asset?")) return
            try { await http.delete(`/assets/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch(e) { this.$toast.error("Error deleting") }
        },
        fmtDate(d) {
            if (!d) return "—"
            return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        },
        openBarcode(item) {
            const codeValue = (item.serialNo || String(item.id)).replace(/[^A-Za-z0-9\-\.\ \$\/\+\%]/g, "").trim() || String(item.id)
            this.barcodeModal = { show: true, item, codeValue }
            this.$nextTick(() => {
                try {
                    JsBarcode(this.$refs.barcodesvg, codeValue, {
                        format: "CODE128",
                        width: 2,
                        height: 60,
                        displayValue: false,
                        margin: 8,
                        background: "#ffffff",
                        lineColor: "#1e293b",
                    })
                } catch (e) {
                    console.error("Barcode generation error", e)
                }
            })
        },
        downloadBarcode() {
            const svg = this.$refs.barcodesvg
            if (!svg) return
            const svgData = new XMLSerializer().serializeToString(svg)
            const canvas = document.createElement("canvas")
            const labelEl = this.$refs.printArea
            canvas.width = labelEl.offsetWidth * 2
            canvas.height = labelEl.offsetHeight * 2
            const ctx = canvas.getContext("2d")
            ctx.scale(2, 2)
            ctx.fillStyle = "#ffffff"
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            const img = new Image()
            const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" })
            const url = URL.createObjectURL(svgBlob)
            img.onload = () => {
                const svgEl = this.$refs.barcodesvg
                const svgRect = svgEl.getBoundingClientRect()
                const labelRect = labelEl.getBoundingClientRect()
                const x = svgRect.left - labelRect.left
                const y = svgRect.top  - labelRect.top
                ctx.drawImage(img, x, y, svgRect.width, svgRect.height)
                URL.revokeObjectURL(url)
                // draw full label using html2canvas fallback — just export SVG as PNG directly
                const finalCanvas = document.createElement("canvas")
                finalCanvas.width  = svgEl.getBoundingClientRect().width  * 3
                finalCanvas.height = svgEl.getBoundingClientRect().height * 3
                const fCtx = finalCanvas.getContext("2d")
                fCtx.scale(3, 3)
                fCtx.fillStyle = "#ffffff"
                fCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)
                fCtx.drawImage(img, 0, 0, svgEl.getBoundingClientRect().width, svgEl.getBoundingClientRect().height)
                const link = document.createElement("a")
                link.download = `barcode-${this.barcodeModal.codeValue}.png`
                link.href = finalCanvas.toDataURL("image/png")
                link.click()
            }
            img.src = url
        },
        printBarcode() {
            const label = this.$refs.printArea
            const svgEl  = this.$refs.barcodesvg
            if (!label || !svgEl) return
            const svgData   = new XMLSerializer().serializeToString(svgEl)
            const svgBytes  = new TextEncoder().encode(svgData)
            const svgB64    = btoa(String.fromCharCode(...svgBytes))
            const html = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Asset Barcode — ${this.barcodeModal.item.name}</title>
                    <style>
                        * { box-sizing: border-box; margin: 0; padding: 0; }
                        body { font-family: 'Segoe UI', sans-serif; background: #fff; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
                        .label { border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 18px 20px; width: 320px; text-align: center; }
                        .tag { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; color: #94a3b8; text-transform: uppercase; margin-bottom: 6px; }
                        .name { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 10px; }
                        .meta-row { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin-bottom: 12px; }
                        .meta { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 5px; padding: 4px 10px; text-align: left; }
                        .meta-label { font-size: 8px; color: #94a3b8; font-weight: 600; text-transform: uppercase; display: block; }
                        .meta-val { font-size: 11px; color: #334155; font-weight: 600; }
                        .barcode-wrap { background: #fff; padding: 6px 0; }
                        .barcode-wrap img { max-width: 100%; }
                        .code { font-size: 10px; color: #64748b; margin-top: 4px; letter-spacing: 1px; font-family: monospace; }
                    </style>
                </head>
                <body>
                    <div class="label">
                        <div class="tag">Asset Tag</div>
                        <div class="name">${this.barcodeModal.item.name}</div>
                        <div class="meta-row">
                            ${this.barcodeModal.item.price ? `<div class="meta"><span class="meta-label">Price</span><span class="meta-val">₹ ${Number(this.barcodeModal.item.price).toLocaleString('en-IN')}</span></div>` : ""}
                            <div class="meta"><span class="meta-label">Added On</span><span class="meta-val">${this.fmtDate(this.barcodeModal.item.createdAt)}</span></div>
                            ${this.barcodeModal.item.serialNo ? `<div class="meta"><span class="meta-label">Serial No</span><span class="meta-val">${this.barcodeModal.item.serialNo}</span></div>` : ""}
                            ${this.barcodeModal.item.allocatedTo ? `<div class="meta"><span class="meta-label">Allocated To</span><span class="meta-val">${this.barcodeModal.item.allocatedTo}</span></div>` : ""}
                        </div>
                        <div class="barcode-wrap">
                            <img src="data:image/svg+xml;base64,${svgB64}" />
                        </div>
                        <div class="code">${this.barcodeModal.codeValue}</div>
                    </div>
                    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};}<\/script>
                </body>
                </html>`
            const blob = new Blob([html], { type: "text/html" })
            const url  = URL.createObjectURL(blob)
            const win  = window.open(url, "_blank", "width=500,height=420")
            win.addEventListener("unload", () => URL.revokeObjectURL(url))
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.as-page { animation: asFade .3s ease; }
@keyframes asFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.as-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.as-header-left { display:flex; align-items:center; gap:10px; }
.as-header-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.as-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.as-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.as-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.as-filter-pills { display:flex; gap:4px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:3px; }
.as-pill { padding:5px 12px; border:none; border-radius:5px; font-size:11.5px; font-weight:600; cursor:pointer; background:transparent; color:$gray500; transition:all $transitionFast; font-family:inherit; &:hover { color:$gray700; } }
.as-pill-active { background:white; color:$primaryColor; box-shadow:$shadowSm; }
.as-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.as-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.as-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:asSpin .7s linear infinite; }
@keyframes asSpin { to { transform:rotate(360deg); } }
.as-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.as-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.as-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.as-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:asSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes asSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.as-card-img-wrap { width:100%; border-radius:6px; overflow:hidden; background:$gray50; border:1px solid $gray100; height:120px; display:flex; align-items:center; justify-content:center; }
.as-card-img { width:100%; height:100%; object-fit:cover; display:block; }
.as-card-top { display:flex; align-items:flex-start; gap:10px; }
.as-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.as-clr-blue { background:#eff6ff; color:#2563eb; }
.as-clr-purple { background:#f5f3ff; color:#7c3aed; }
.as-clr-teal { background:#f0fdfa; color:#0d9488; }
.as-clr-amber { background:#fffbeb; color:#d97706; }
.as-clr-rose { background:#fff1f2; color:#e11d48; }
.as-card-info { flex:1; min-width:0; }
.as-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0 0 3px; line-height:1.3; }
.as-card-serial { font-size:11px; color:$gray400; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.as-badge { font-size:10px; font-weight:700; padding:3px 8px; border-radius:$radiusFull; flex-shrink:0; align-self:flex-start; }
.as-badge-active { background:#f0fdf4; color:#16a34a; }
.as-badge-inactive { background:$gray50; color:$gray400; }
.as-card-details { display:flex; flex-wrap:wrap; gap:6px; }
.as-detail { flex:1; min-width:70px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; display:flex; flex-direction:column; gap:2px; }
.as-detail-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.as-detail-val { font-size:12px; color:$gray700; font-weight:600; }
.as-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.as-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.as-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.as-btn-logs { color:#7c3aed; &:hover { background:#f5f3ff; border-color:rgba(124,58,237,0.15); } }
.as-btn-bill { color:#0d9488; text-decoration:none; &:hover { background:#f0fdfa; border-color:rgba(13,148,136,0.15); } }
.as-btn-barcode { color:#0369a1; &:hover { background:#f0f9ff; border-color:rgba(3,105,161,0.15); } }
.as-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .as-grid { grid-template-columns:repeat(2,1fr); } .as-header { flex-direction:column; align-items:stretch; } .as-header-right { justify-content:flex-end; } }
@media(max-width:480px) { .as-grid { grid-template-columns:1fr; } }

/* ── Barcode Modal ── */
.bc-overlay {
    position:fixed; inset:0; background:rgba(15,23,42,0.5); backdrop-filter:blur(3px);
    z-index:3000; display:flex; align-items:center; justify-content:center; padding:16px;
}
.bc-modal {
    background:white; border-radius:14px; width:100%; max-width:400px;
    box-shadow:0 24px 60px rgba(0,0,0,0.2); overflow:hidden;
}
.bc-modal-header {
    display:flex; align-items:center; gap:10px; padding:14px 16px;
    border-bottom:1px solid $gray100;
}
.bc-modal-icon {
    width:32px; height:32px; border-radius:8px; background:#f0f9ff; color:#0369a1;
    display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0;
}
.bc-modal-title { font-size:14px; font-weight:700; color:$gray800; flex:1; }
.bc-close {
    width:28px; height:28px; border:1px solid $gray200; border-radius:6px; background:white;
    color:$gray400; cursor:pointer; display:flex; align-items:center; justify-content:center;
    font-size:11px; transition:all $transitionFast;
    &:hover { background:$gray50; color:$gray600; }
}

/* Label area (what gets printed) */
.bc-label {
    padding:18px 20px 10px; text-align:center; background:white;
}
.bc-label-header { margin-bottom:4px; }
.bc-company-name {
    font-size:9px; font-weight:700; letter-spacing:2px; color:$gray400;
    text-transform:uppercase;
}
.bc-asset-name {
    font-size:16px; font-weight:700; color:$gray800; margin:4px 0 12px;
    line-height:1.2;
}
.bc-meta-row {
    display:flex; flex-wrap:wrap; gap:6px; justify-content:center; margin-bottom:14px;
}
.bc-meta {
    background:$gray50; border:1px solid $gray100; border-radius:5px;
    padding:4px 10px; text-align:left;
}
.bc-meta-label {
    font-size:8px; color:$gray400; font-weight:600; text-transform:uppercase;
    letter-spacing:0.4px; display:block;
}
.bc-meta-val { font-size:11px; color:$gray700; font-weight:600; }
.bc-svg-wrap {
    display:flex; justify-content:center; background:white; padding:4px 0;
    svg { max-width:100%; }
}
.bc-code-text {
    font-size:10px; color:$gray400; letter-spacing:2px; font-family:monospace;
    margin-top:4px; padding-bottom:6px;
}

/* Actions */
.bc-actions {
    display:flex; gap:8px; padding:12px 16px;
    border-top:1px solid $gray100; background:$gray50; justify-content:flex-end;
}
.bc-btn {
    display:inline-flex; align-items:center; gap:6px; padding:7px 14px;
    border-radius:6px; font-size:12px; font-weight:600; cursor:pointer;
    font-family:inherit; transition:all $transitionFast;
    i { font-size:11px; }
}
.bc-btn-download {
    border:1px solid $gray200; background:white; color:$gray600;
    &:hover { background:$gray100; }
}
.bc-btn-print {
    border:none; background:$primaryColor; color:white;
    &:hover { background:$primaryDark; }
}

/* Modal transition */
.bc-fade-enter-active { transition:opacity .2s ease, transform .2s ease; }
.bc-fade-leave-active { transition:opacity .15s ease, transform .15s ease; }
.bc-fade-enter, .bc-fade-leave-to { opacity:0; }
.bc-fade-enter .bc-modal, .bc-fade-leave-to .bc-modal { transform:scale(0.96) translateY(6px); }
</style>

<style></style>
